// Who is signed in, the API they call through, what they may do, and the loading hook every screen uses.
import React, { createContext, useContext, useEffect, useState } from "react";
import { isAdminRole, isDealerRole, permissionsOf, writeScope as scopeOf, type LensUser, type Me, type Permission, type WriteScope } from "@phoneware/lens-domain";
import type { LensApi } from "./api.js";
import type { AuthClient } from "./auth.js";
import { ApiError } from "./api.js";

/** What kind of person a screen is for. A customer kind adds an arm here and every switch over it stops compiling. */
export type ViewerKind = "admin" | "dealer";

/**
 * The one kind test the screens use (spec 3.3). "Not an admin" is never "a dealer user": every branch on the kind
 * switches over this and ends in a `never` arm.
 */
export function viewerKind(user: Pick<LensUser, "role">): ViewerKind {
  if (isAdminRole(user.role)) return "admin";
  if (isDealerRole(user.role)) return "dealer";
  const unhandled: never = user.role;
  throw new Error(`Unknown role ${String(unhandled)}`);
}

/** What the signed-in person may do. Screens ask these, never the role. */
export interface Access {
  /** True when the person holds the permission at all. */
  can: (p: Permission) => boolean;
  /** Where the person may use a write permission: "own" dealer, the "house" dealer, "any" dealer, or null for nowhere. */
  writeScope: (p: Permission) => WriteScope | null;
  /** True when the person may use a write permission on this dealer's records. */
  canWrite: (p: Permission, dealer: { id: string; house?: boolean } | null) => boolean;
  /** Sees every dealer's records: the Phoneware side of Omni. */
  everyDealer: boolean;
  /** Sees prices: quotes, the catalog and order totals. A dealer's tech does not. */
  seesPrices: boolean;
}

/** Whether someone with these permissions sees prices (quotes, catalog, totals). */
export function seesPricesWith(permissions: readonly Permission[]): boolean {
  return permissions.includes("catalog.read_all") || permissions.includes("quotes.write");
}

export interface Session extends Access {
  me: Me;
  api: LensApi;
  /** The sign-in client: how this person signs in (Settings, Sign-in) and adding Google or Microsoft. */
  auth: AuthClient;
  signOut: () => void;
  /** Re-reads /api/me, after the caller changed their own dealer or user. */
  refreshMe: () => void;
  /** A Super admin starts acting as a dealer user (POST /api/act-as) and lands on their Today. */
  startActingAs: (email: string) => Promise<void>;
  /**
   * Stops acting (DELETE /api/act-as/:id, "stopped"), returns to the dealer the session started from, or to `landing`,
   * and answers with the line to show: who it was and how many changes were recorded.
   */
  stopActing: (landing?: string) => Promise<string>;
}

/**
 * The permissions come from GET /api/me when the server sends them, otherwise from the
 * role through the one definition in @phoneware/lens-domain. Either way the server
 * decides again on every request; this only decides what the screen offers.
 */
export function accessFor(me: Me): Access {
  const held = new Set<Permission>(me.permissions ?? permissionsOf(me.user.role));
  const can = (p: Permission) => held.has(p);
  const writeScope = (p: Permission): WriteScope | null => (held.has(p) ? scopeOf(me.user.role, p) : null);
  const canWrite = (p: Permission, dealer: { id: string; house?: boolean } | null): boolean => {
    const scope = writeScope(p);
    if (scope === "any") return true;
    if (!dealer) return false;
    if (scope === "house") return dealer.house === true;
    if (scope === "own") return dealer.id === me.user.dealerId;
    return false;
  };
  return { can, writeScope, canWrite, everyDealer: can("all_dealers.read"), seesPrices: seesPricesWith([...held]) };
}

const SessionContext = createContext<Session | null>(null);

export const SessionProvider = SessionContext.Provider;

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession outside a signed-in screen");
  return session;
}

export interface Loaded<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** Loads again, keeping what is on screen until the answer arrives. */
  reload: () => void;
  /** Replaces what is on screen with a record the server just returned. */
  set: (value: T) => void;
}

export function useLoad<T>(load: () => Promise<T>, deps: React.DependencyList): Loaded<T> {
  const [state, setState] = useState<{ data: T | null; error: Error | null; loading: boolean }>({ data: null, error: null, loading: true });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    load().then(
      (data) => {
        if (live) setState({ data, error: null, loading: false });
      },
      (error: unknown) => {
        if (live) setState((s) => ({ data: s.data, error: error instanceof Error ? error : new Error(String(error)), loading: false }));
      },
    );
    return () => {
      live = false;
    };
    // The caller's deps decide when to load; `load` is a fresh closure every render.
  }, [...deps, round]);
  return {
    ...state,
    reload: () => setRound((r) => r + 1),
    set: (data) => setState({ data, error: null, loading: false }),
  };
}

/** A detail record the caller may not see: the server answers 404 (or 403) instead of someone else's data. */
export function isNotYours(error: Error | null): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 403);
}
